import { EventEmitter, EVENTS } from './store/EventEmitter'
import {
  PropInfo,
  Section,
  SectionFactory,
} from './components/sections/TextSection'
import { SectionBase } from './components/sections/SectionBase'
import { changeType, StateManager } from './store/StateManager'
import { DomHelper } from './utlis/DomHelper'
import { DomManager } from './managers/DOMManager'
import { LayoutManager } from './LayoutManager'
import { CSS_CLASSES, SELECTORS } from './constants/CSSClasses'
import { DragSelector } from './components/DragSelector'
import { ContextMenu, MenuItem } from './components/ContextMenu'
import { ScaleRuler } from './components/controls/ruler/Ruler'

export interface IToolDroppedEventsArgs {
  data: { x: number; y: number; type: string } // Drag
}

export class DragDropManager {
  host: HTMLElement
  event: EventEmitter<any>
  currentZoomScale:[number,number] = [1,1]
  dragElement: HTMLElement | null = null
  dragSelector: DragSelector | null = null
  contextMenu: ContextMenu | null = null
  isDragging: boolean = false
  isResizing: boolean = false
  cursorX: number = 0
  cursorY: number = 0
  initialX: number = 0
  initialY: number = 0
  offsetX: number = 0
  offsetY: number = 0
  resizeHandle: string | null = null
  stateManager: StateManager
  private horizontalLine!: HTMLElement
  private verticalLine!: HTMLElement
  private clipboard: Section[]
  private selectedElements: HTMLElement[] = []
  private selectedUnlockedElements: HTMLElement[] = []
  private initialPositions: { id: string; left: number; top: number }[] = []
  private domManager: DomManager
  private initialWidth: number = 0;
  private initialHeight: number = 0;
  private initialResizeLeft: number = 0;
  private initialResizeTop: number = 0;
  private initialResizeRight: number = 0;
  private initialResizeBottom: number = 0;
  constructor(host: HTMLElement) {
    this.host = host
    this.clipboard = []
    this.event = EventEmitter.getInstance()
    this.stateManager = StateManager.getInstance()
    this.domManager = DomManager.getInstance()
    // Create alignment lines using DOMManager
    const {
      horizontalLine,
      verticalLine,
    } = this.domManager.createAlignmentLines(host)
    this.horizontalLine = horizontalLine
    this.verticalLine = verticalLine

    this.init(host)
    ;(window as any).dp = this
    this.initializeKeyboardShortcuts()    
  }
  
  private isFocusedInsideViewPort() {
    const activeElement = document.activeElement as HTMLElement | null
    if (!activeElement) return false
    let sectionEl = activeElement.closest(SELECTORS.LAYOUT_DRAG_ITEM)
    if (sectionEl) return true

    // return true;
    const classList = activeElement.classList
    return (
      classList.contains(CSS_CLASSES.DRAGGABLE_CONTAINER) ||
      activeElement.id === CSS_CLASSES.ID.VIEWPORT_CONTAINER ||
      activeElement.id === CSS_CLASSES.ID.VIEWPORT ||
      classList.contains(CSS_CLASSES.LAYOUT_DRAG_ITEM)
    )
  }

  private handleCopy() {
    this.clipboard = this.stateManager.getSelectedSections()
    this.clipboard.forEach((x) => {
      x.col += 1
      1
      x.row += 1
    })
  }

  private handlePaste() {
    if (!this.clipboard.length) return
    const state = this.stateManager.getState()
    const sectionAdded: Section[] = []

    this.clipboard.forEach((section) => {
      //assign new id as it is a new section
      section.id = DomHelper.getId({
        existing: state.sections.map((x) => x.id),
      })
      sectionAdded.push(LayoutManager.addSection(section))
    })
    // this.addOrUpdateSectionToState(sectionAdded as SectionBase,)
    this.setSectionsInState(
      [...state.sections, ...sectionAdded],
      changeType.SECTION_PASTED,
      sectionAdded.map((x) => x.id),
    )
  }

  private init(host: HTMLElement): void {
    host.addEventListener('dragover', (e) => e.preventDefault())
    const toolboxPanel = host.querySelector(
      `.${CSS_CLASSES.TOOLBOX_PANEL}`,
    ) as HTMLElement | null
    if (toolboxPanel) {
      host.addEventListener('dragstart', (e) => {
        const target = e.target as HTMLElement
        if (target.classList.contains(CSS_CLASSES.TOOLBOX_DRAG_ITEM)) {
          this.startDragFromToolbox(e, target)
        }
      })
    }

    // Attach mouse events globally
    document.addEventListener('mousedown', (e) => this.onMouseDown(e), true)
    document.addEventListener('mouseup', (e) => this.onMouseUp(e))
    document.addEventListener('mousemove', (e) => this.onMouseMove(e))
  }

  private onMouseDown(e: MouseEvent): void {
    this.contextMenu = null;
    if (!e.target || !(e.target as any).closest) return
    let target = (e.target as HTMLDivElement).closest(
      SELECTORS.LAYOUT_DRAG_ITEM,
    ) as HTMLDivElement
    let targetGrid:null|HTMLDivElement = (e.target as HTMLElement).closest('#canvas')  as HTMLDivElement;
    if (!target && !targetGrid) return
    if(target){
      if (target.classList.contains(CSS_CLASSES.LAYOUT_DRAG_ITEM)) {
        if (e.ctrlKey) {
          if(e.button == 0)
          {
            this.toggleSelection(target)
            if(!targetGrid)
              targetGrid = this.domManager.getViewportGridLayerContainer();
            if(targetGrid){
              this.dragSelector = new DragSelector(targetGrid);
              this.dragSelector.handleMouseDown(e, this.currentZoomScale);
              this.dragSelector.isDragging = true;
            }
          }
          if(e.button == 2){
            targetGrid = null;
          }
        } else {
          // Don't clear other selections if clicking on an already selected element
          const isTargetSelected = target.classList.contains(CSS_CLASSES.SELECTED)
          const hasOtherSelections =
            DomHelper.getAllEl(SELECTORS.SELECTED_DRAG_ITEM).length > 1

          if (!isTargetSelected || !hasOtherSelections) {
            this.changeSelection(target, true, e)
          }

          // Store all selected elements
          this.selectedElements = Array.from(
            document.querySelectorAll(SELECTORS.SELECTED_DRAG_ITEM),
          )

          // store all selected unlocked elements and their initial positions
          this.selectedUnlockedElements = Array.from(
            document.querySelectorAll(SELECTORS.SELECTED_UNLOCKED_DRAG_ITEM),
          )

          this.initialPositions = this.selectedUnlockedElements.map((el) => ({
            id: el.id,
            left: parseInt(el.style.left) || 0,
            top: parseInt(el.style.top) || 0,
          }))
          if(e.button == 0){
            this.dragElement = target
            this.isDragging = true
            this.cursorX = e.pageX
            this.cursorY = e.pageY

            // Calculate offset based on the clicked element
            const rect = target.getBoundingClientRect()
            this.initialX = rect.left
            this.initialY = rect.top
            this.offsetX = this.cursorX - this.initialX
            this.offsetY = this.cursorY - this.initialY
          }
          else if(e.button == 2){
            this.contextMenu = new ContextMenu(e.target as HTMLElement,[
              new MenuItem("Lock", ()=>{
                this.handleLock();
              }),
              new MenuItem("Unock", ()=>{
                this.handleUnlock();
              }),
              new MenuItem("Bring to Front", ()=>{
                const selSections = this.stateManager.getSelectedSections();
                if(selSections.length>0){
                  const state = this.stateManager.getState();
                  const index = this.stateManager.getIndexOfSection(selSections[0]);
                  state.sections.splice(index,selSections.length);
                  let sectionIds:string[] = []
                  selSections.forEach(s => {
                    state.sections.push(s);  
                    sectionIds.push(s.id);
                    LayoutManager.bringToFront(s.id);
                  });                  
                  this.stateManager.setState({sectionIds:sectionIds,state:state,changeType:changeType.BRING_TO_FRONT});                  
                }
              }),              
              new MenuItem("Send to Back", ()=>{
                const selSections = this.stateManager.getSelectedSections();
                if(selSections.length>0){
                  const state = this.stateManager.getState();                  
                  const index = this.stateManager.getIndexOfSection(selSections[0]);
                  state.sections.splice(index,selSections.length);
                  let sectionIds:string[] = []
                  selSections.reverse().forEach(s => {               
                    state.sections.unshift(s);
                    sectionIds.unshift(s.id);
                    LayoutManager.sendToBack(s.id);
                  });                  
                  this.stateManager.setState({sectionIds:sectionIds,state:state,changeType:changeType.SEND_TO_BACK});                  
                }
              })
            ]);
          }
        }
      }

      if ((e.target as any).classList.contains(CSS_CLASSES.RESIZE_HANDLE)) {
        if(!e.ctrlKey && e.button == 0){
          this.dragElement = (e.target as any).parentElement as HTMLElement
          this.changeSelection(this.dragElement, true)
          this.isResizing = true
          this.resizeHandle = (e.target as any).classList[1]
          this.cursorX = e.pageX
          this.cursorY = e.pageY
          
          // Store initial dimensions and position
          this.initialWidth = this.dragElement.offsetWidth
          this.initialHeight = this.dragElement.offsetHeight
          this.initialResizeLeft = this.dragElement.offsetLeft
          this.initialResizeTop = this.dragElement.offsetTop
        }
      }
    }
    else if(targetGrid){
      this.unSelectAll();
      this.dragSelector = new DragSelector(targetGrid);
      this.dragSelector.handleMouseDown(e);
      this.dragSelector.isDragging = true;
    }
  }

  private toggleSelection(target: HTMLElement): void {
    target.classList.toggle(CSS_CLASSES.SELECTED)
    let currentState = this.stateManager.getState()
    currentState.sections.forEach((section) => {
      if (section.id === target.id) {
        section.selected = !section.selected
      }
    })

    this.stateManager.setState({
      targetEl: target,
      sectionIds: [target.id],
      state: { ...currentState },
      changeType: changeType.SELECTION_CHANGE,
    })
  }

  private unSelectAll(){
    this.domManager.clearSelections();
    let currentState = this.stateManager.getState();
    currentState.sections.forEach(s=>{
      s.selected = false;
    })
    DomHelper.getAllDragItems().forEach((el) => {
      el.classList.remove(CSS_CLASSES.SELECTED)
    })
    this.stateManager.setState({
      targetEl: undefined,
      sectionIds: [],
      state: currentState,
      changeType: changeType.SELECTION_CHANGE,
    })
  }

  private changeSelection(
    target: HTMLElement,
    needToSelect: boolean,
    event?: MouseEvent,
  ): void {
    let currentState = this.stateManager.getState()

    // Only clear other selections if the target isn't already selected
    if (!target.classList.contains(CSS_CLASSES.SELECTED)) {
      if (!needToSelect) {
        // Clear all selections
        currentState.sections.forEach((section) => (section.selected = false))
        DomHelper.getAllDragItems().forEach((el) =>
          el.classList.remove(CSS_CLASSES.SELECTED),
        )
      } else if (event && !event.ctrlKey) {
        // Clear other selections
        currentState.sections.forEach((section) => {
          if (section.id !== target.id) section.selected = false
        })
        DomHelper.getAllDragItems().forEach((el) => {
          if (el.id !== target.id) el.classList.remove(CSS_CLASSES.SELECTED)
        })
      }
    }

    if (needToSelect) {
      target.classList.add(CSS_CLASSES.SELECTED)
      currentState.sections.forEach((section) => {
        if (section.id === target.id) {
          section.selected = true
        }
      })
    }

    this.stateManager.setState({
      targetEl: target,
      sectionIds: [target.id],
      state: currentState,
      changeType: changeType.SELECTION_CHANGE,
    })
  }

  private startDragFromToolbox(
    e: DragEvent | MouseEvent,
    target: HTMLElement,
  ): void {
    e.preventDefault()
    this.dragElement = SectionFactory.createSectionElement()

    if ((target as any)['sectionObj']) {
      var sectionObj: SectionBase = (target as any)['sectionObj'].clone()
      sectionObj.id = this.dragElement.id
      sectionObj.selected = false
      this.dragElement.focus()
      if (sectionObj instanceof SectionBase) {
        // get the correct section object from its type
        sectionObj = SectionFactory.getSectionByType(
          sectionObj.toJSON(),
        ) as SectionBase
      }
      // Setup the drag element
      ;(this.dragElement as any)['sectionObj'] = sectionObj
      this.dragElement.appendChild(sectionObj.render())
      if(sectionObj.isLocked){
        this.dragElement.classList.add(CSS_CLASSES.LOCKED);
      }
      // Set initial dimensions based on grid
      const state = this.stateManager.getState()
      this.dragElement.style.width = `${
        state.cellWidth * sectionObj.colCount
      }px`
      this.dragElement.style.height = `${
        state.cellHeight * sectionObj.rowCount
      }px`
      const scale = (state.scale)?state.scale:[1,1]
      const zoomValue = this.stateManager.getZoomValue();
      DomManager.setZoom(this.dragElement as HTMLDivElement, [zoomValue * scale[0] ,zoomValue * scale[1]])
      // Apply properties
      sectionObj.propInfo?.forEach((propInfo: PropInfo) => {
        DomHelper.setBindingValue(
          this.dragElement?.querySelector(SELECTORS.SECTION),
          propInfo.binding,
          this.getValue(propInfo.value),
        )
      })

      // Initialize drag operation
      this.addResizeHandles(this.dragElement)
      DomHelper.getEl(SELECTORS.DRAGGABLE_CONTAINER)?.appendChild(
        this.dragElement,
      )
      this.isDragging = true
      this.cursorX = e.pageX
      this.cursorY = e.pageY

      // Center the element under cursor
      const rect = this.dragElement.getBoundingClientRect()
      this.offsetX = rect.width / 2
      this.offsetY = rect.height / 2

      // Set initial position
      this.updateDragElementPosition(e.pageX, e.pageY)

      // Add this element to selected elements
      this.selectedElements = [this.dragElement]
      this.selectedUnlockedElements = [this.dragElement]
      this.initialPositions = [
        {
          id: this.dragElement.id,
          left: parseInt(this.dragElement.style.left) || 0,
          top: target.offsetTop - 10,
        },
      ]
    }
  }

  private addResizeHandles(element: HTMLElement): void {
    const handles = [
      CSS_CLASSES.RESIZE_TOP_LEFT,
      CSS_CLASSES.RESIZE_TOP_RIGHT,
      CSS_CLASSES.RESIZE_BOTTOM_LEFT,
      CSS_CLASSES.RESIZE_BOTTOM_RIGHT,
    ]
    handles.forEach((handle) => {
      const resizeHandle = document.createElement('div')
      resizeHandle.classList.add(CSS_CLASSES.RESIZE_HANDLE, handle)
      element.appendChild(resizeHandle)
    })
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.isDragging && !this.isResizing && !this.dragSelector?.isDragging) return;

    e.preventDefault();
    
    // Store the latest mouse position
    const mouseX = e.pageX;
    const mouseY = e.pageY;

    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(() => {
      if (this.isDragging) {
        this.snapToGrid(mouseX, mouseY);
        this.checkAlignment(mouseX, mouseY);
      }

      if (this.isResizing) {
        this.updateResizeElement(mouseX, mouseY);
      }

      if (this.dragSelector?.isDragging) {
        this.dragSelector.handleMouseMove(e,this.currentZoomScale);
      }
    });
  }

  private snapToGrid(pageX: number, pageY: number): void {
    if (!this.dragElement) return
    let scale = this.stateManager.getState().scale
    if(!scale)
      scale = [1,1];
    const deltaX = (pageX - this.cursorX) / this.currentZoomScale[0]
    const deltaY = (pageY - this.cursorY) / this.currentZoomScale[1]
    const viewportContainer = DomHelper.getEl(`#${CSS_CLASSES.ID.VIEWPORT_CONTAINER}`) as HTMLElement
    const cellWidth = (this.stateManager.getState().cellWidth / this.stateManager.getMultipler()) * scale[0] * this.stateManager.getZoomValue()
    const cellHeight = (this.stateManager.getState().cellHeight / this.stateManager.getMultipler()) * scale[1] * this.stateManager.getZoomValue()

    // Calculate new positions for each element
    this.selectedUnlockedElements.forEach((element, index) => {
      const initialPos = this.initialPositions[index]
      let newLeft = Math.round((initialPos.left + deltaX) / cellWidth) * cellWidth 
      let newTop = Math.round((initialPos.top + deltaY) / cellHeight) * cellHeight 

      // Clamp values to viewport boundaries
      if(!scale)
        scale = [1,1];
      newLeft = Math.max(0, Math.min(newLeft, viewportContainer.clientWidth - (element.offsetWidth * scale[0] * this.stateManager.getZoomValue())))
      newTop = Math.max(0, Math.min(newTop, viewportContainer.clientHeight - (element.offsetHeight * scale[1] * this.stateManager.getZoomValue())))

      // Apply the clamped positions
      element.style.left = `${newLeft}px`
      element.style.top = `${newTop}px`
    })
  }

  private updateResizeElement(pageX: number, pageY: number): void {
    if (!this.dragElement || !this.resizeHandle) return;
    const state = this.stateManager.getState()
    const scale = (state.scale)?state.scale:[1,1]
    const zoomValue = this.stateManager.getZoomValue();
    const viewportContainer = DomHelper.getEl(`#${CSS_CLASSES.ID.VIEWPORT_CONTAINER}`) as HTMLElement
    const containersToUpdate = DomHelper.getAllEl(SELECTORS.SELECTED_UNLOCKED_DRAG_ITEM)

    // Calculate the total movement from initial position
    const totalDeltaX = pageX - this.cursorX;
    const totalDeltaY = pageY - this.cursorY;

    containersToUpdate.forEach((container) => {
        const element = container as HTMLElement
        let newWidth = this.initialWidth;
        let newHeight = this.initialHeight;
        let newLeft = this.initialResizeLeft;
        let newTop = this.initialResizeTop;
        let newRight = this.initialResizeLeft + this.initialWidth;
        let newBottom = this.initialResizeTop + this.initialHeight;

        switch (this.resizeHandle) {
            case CSS_CLASSES.RESIZE_TOP_LEFT:
                newWidth = this.initialWidth - (totalDeltaX / (scale[0] * zoomValue));
                newHeight = this.initialHeight - (totalDeltaY / (scale[1] * zoomValue));
                newLeft = this.initialResizeLeft + (totalDeltaX / this.currentZoomScale[0]);
                newTop = this.initialResizeTop + (totalDeltaY / this.currentZoomScale[1]);
                break;
                
            case CSS_CLASSES.RESIZE_TOP_RIGHT:
                newWidth = this.initialWidth + (totalDeltaX / (scale[0] * zoomValue));
                newHeight = this.initialHeight - (totalDeltaY / (scale[1] * zoomValue));
                newTop = this.initialResizeTop + (totalDeltaY / this.currentZoomScale[1]);
                break;
                
            case CSS_CLASSES.RESIZE_BOTTOM_LEFT:
                newWidth = this.initialWidth - (totalDeltaX / (scale[0] * zoomValue));
                newHeight = this.initialHeight + (totalDeltaY / (scale[1] * zoomValue));
                newLeft = this.initialResizeLeft + (totalDeltaX / this.currentZoomScale[0]);
                break;
                
            case CSS_CLASSES.RESIZE_BOTTOM_RIGHT:
                newWidth = this.initialWidth + (totalDeltaX / (scale[0] * zoomValue));
                newHeight = this.initialHeight + (totalDeltaY / (scale[1] * zoomValue));
                break;
        }

        // Enforce minimum size
        let minSize = Math.min(this.stateManager.getState().cellWidth, this.stateManager.getState().cellHeight);
        if(minSize < 10)
          minSize = 10;
        newWidth = Math.max(minSize, newWidth);
        newHeight = Math.max(minSize, newHeight);

        // Apply viewport constraints
        if (newLeft < 0) {
            newWidth += newLeft;
            newLeft = 0;
        }
        if (newTop < 0) {
            newHeight += newTop;
            newTop = 0;
        }

        // Right boundary
        if (newLeft + (newWidth * scale[0] * this.stateManager.getZoomValue()) > viewportContainer.offsetWidth) {
            newWidth = (viewportContainer.offsetWidth - newLeft) / (scale[0] * this.stateManager.getZoomValue());
        }
        // Bottom boundary
        if (newTop + (newHeight * scale[1] * this.stateManager.getZoomValue())> viewportContainer.offsetHeight) {
            newHeight = (viewportContainer.offsetHeight - newTop) / (scale[1] * this.stateManager.getZoomValue());
        }
        // Apply the final dimensions and position
        element.style.width = `${Math.max(minSize, newWidth)}px`;
        element.style.height = `${Math.max(minSize, newHeight)}px`;
        element.style.left = `${newLeft}px`;
        element.style.top = `${newTop}px`;
    });
  }

  private checkAlignment(pageX: number, pageY: number): void {
    const viewportContainer = DomHelper.getEl(
      `#${CSS_CLASSES.ID.VIEWPORT}`,
    ) as HTMLElement
    const viewportRect = viewportContainer.getBoundingClientRect()
    const draggedRect = this.dragElement!.getBoundingClientRect()
    let showHorizontal = false
    let showVertical = false
    const EPSILON = 2 // Tolerance for alignment

    DomHelper.getAllEl(SELECTORS.LAYOUT_DRAG_ITEM).forEach((draggable) => {
      if (draggable === this.dragElement) return

      const rect = (draggable as HTMLElement).getBoundingClientRect()

      // Horizontal alignment (top-to-top, top-to-bottom, bottom-to-bottom)
      if (Math.abs(draggedRect.top - rect.top) <= EPSILON) {
        if (this.horizontalLine) {
          this.horizontalLine.style.top = `${
            (rect.top - viewportRect.top + viewportContainer.scrollTop) / this.currentZoomScale[1]
          }px`
          this.horizontalLine.style.display = 'block'
          showHorizontal = true
        }
      }
      if (Math.abs(draggedRect.bottom - rect.top) <= EPSILON) {
        if (this.horizontalLine) {
          this.horizontalLine.style.top = `${
            (rect.top - viewportRect.top + viewportContainer.scrollTop) / this.currentZoomScale[1]
          }px`
          this.horizontalLine.style.display = 'block'
          showHorizontal = true
        }
      }
      else if(Math.abs(draggedRect.bottom - rect.bottom) <= EPSILON){
        if (this.horizontalLine) {
          this.horizontalLine.style.top = `${
            (rect.bottom - viewportRect.top + viewportContainer.scrollTop) / this.currentZoomScale[1]
          }px`
          this.horizontalLine.style.display = 'block'
          showHorizontal = true
        }
      } 
      else if (Math.abs(draggedRect.top - rect.bottom) <= EPSILON) {
        if (this.horizontalLine) {
          this.horizontalLine.style.top = `${
            (rect.bottom - viewportRect.top + viewportContainer.scrollTop) / this.currentZoomScale[1]
          }px`
          this.horizontalLine.style.display = 'block'
          showHorizontal = true
        }
      }

      // Vertical alignment (left-to-left, left-to-right, right-to-right)
      if (Math.abs(draggedRect.left - rect.left) <= EPSILON) {
        if (this.verticalLine) {
          this.verticalLine.style.left = `${
            (rect.left - viewportRect.left + viewportContainer.scrollLeft) / this.currentZoomScale[0]
          }px`
          this.verticalLine.style.display = 'block'
          showVertical = true
        }
      } 
      if (Math.abs(draggedRect.right - rect.left) <= EPSILON) {
        if (this.verticalLine) {
          this.verticalLine.style.left = `${
            (rect.left - viewportRect.left + viewportContainer.scrollLeft) / this.currentZoomScale[0]
          }px`
          this.verticalLine.style.display = 'block'
          showVertical = true
        }
      } 
      else if(Math.abs(draggedRect.right - rect.right) <= EPSILON){
        if (this.verticalLine) {
          this.verticalLine.style.left = `${
            (rect.right - viewportRect.left + viewportContainer.scrollLeft) / this.currentZoomScale[0]
          }px`
          this.verticalLine.style.display = 'block'
          showVertical = true
        }
      }
      else if (Math.abs(draggedRect.left - rect.right) <= EPSILON) {
        if (this.verticalLine) {
          this.verticalLine.style.left = `${
            (rect.right - viewportRect.left + viewportContainer.scrollLeft) / this.currentZoomScale[0]
          }px`
          this.verticalLine.style.display = 'block'
          showVertical = true
        }
      }
    })

    // Hide lines if no alignment was found
    if (!showHorizontal && this.horizontalLine) {
      this.horizontalLine.style.display = 'none'
    }

    if (!showVertical && this.verticalLine) {
      this.verticalLine.style.display = 'none'
    }
  }

  private onMouseUp(e: MouseEvent): void {
    if (!this.isDragging && !this.isResizing && !this.dragSelector) return

    e.preventDefault()
    e.stopPropagation()
    // If the mouse hasn't moved significantly, treat it as a click instead of a drag or resize
    if (
      Math.abs(this.cursorX - e.pageX) < 5 &&
      Math.abs(this.cursorY - e.pageY) < 5 &&
      !this.isResizing
    ) {
      this.isDragging = false
      this.isResizing = false
      this.dragElement = null
      return
    }
    if (this.isDragging) {
      e.preventDefault()
      if (this.dragElement) {
        const viewportContainer = DomHelper.getEl(
          `#${CSS_CLASSES.ID.VIEWPORT_CONTAINER}`,
        ) as HTMLElement
        const viewportRect = viewportContainer.getBoundingClientRect()
        const rect = this.dragElement.getBoundingClientRect()

        if (
          rect.left > viewportRect.right ||
          rect.top > viewportRect.bottom ||
          rect.right < viewportRect.left ||
          rect.bottom < viewportRect.top
        ) {
          this.removeSectionFromState(this.dragElement.id)
          this.dragElement.remove()
        } else {
          const updatedSections: SectionBase[] = []
          const state = this.stateManager.getState();
          const scale = (state.scale)?state.scale:[1,1]
          this.selectedElements.forEach((element) => {
            const rect = element.getBoundingClientRect()
            const cellWidth = (state.cellWidth / this.stateManager.getMultipler()) * scale[0] * this.stateManager.getZoomValue()
            const cellHeight = (state.cellHeight / this.stateManager.getMultipler()) * scale[1] * this.stateManager.getZoomValue()

            const newLeft =
              Math.round(
                (rect.left - viewportRect.left) / 
                  cellWidth,
              ) * cellWidth / this.currentZoomScale[0]
            const newTop =
              Math.round(
                (rect.top - viewportRect.top) / 
                  cellHeight,
              ) * cellHeight / this.currentZoomScale[1]

            element.style.left = `${newLeft}px`
            element.style.top = `${newTop}px`
            let sectionObj = (element as any)['sectionObj']
            sectionObj.x = newLeft
            sectionObj.y = newTop
            sectionObj.row = Math.round(newTop / cellHeight)
            sectionObj.col = Math.round(newLeft / cellWidth)
            sectionObj.rowCount = Math.round(
              rect.height / state.cellHeight,
            )
            sectionObj.colCount = Math.round(
              rect.width / state.cellWidth,
            )
            sectionObj.width = rect.width
            sectionObj.height = rect.height
            sectionObj.selected = true
            LayoutManager.syncSectionToDom(sectionObj)
            //remove all the selection
            this.changeSelection(e.target as HTMLElement, false)
            element.focus()
            element.classList.add(CSS_CLASSES.SELECTED)
            // sectionObj.propInfo.forEach((propInfo: PropInfo) => {
            //   console.log("propInfo",propInfo);
            //   propInfo.value = DomHelper.getBindingValue(element, propInfo.binding);
            // });
            updatedSections.push(this.fitToGrid(element as HTMLElement) as SectionBase,)
          })
          this.addOrUpdateSectionToState(
            updatedSections,
            changeType.UPDATE_RESIZE_INFO,
            updatedSections.map((x) => x.id),
          )
        }
      }

      this.isDragging = false
      this.dragElement = null
    }
    if (this.isResizing) {
      e.preventDefault()
      const containers = DomHelper.getAllEl(
        `.${CSS_CLASSES.LAYOUT_DRAG_ITEM}.${CSS_CLASSES.SELECTED}`,
      )
      const ids: string[] = []
      const updatedSectionObjects: SectionBase[] = []
      containers.forEach((x) => {
        ids.push(x.id)
        updatedSectionObjects.push(
          this.fitToGrid(x as HTMLElement, true) as SectionBase,
        )
      })
      this.addOrUpdateSectionToState(
        updatedSectionObjects,
        changeType.UPDATE_RESIZE_INFO,
        ids,
      )
      this.isResizing = false
      this.dragElement = null
      this.resizeHandle = null
    }
    this.horizontalLine.style.display = 'none'
    this.verticalLine.style.display = 'none'
    if(this.dragSelector && this.dragSelector.isDragging){
        const selectionBoxRect = this.dragSelector.handleMouseUp(this.currentZoomScale);
        if(selectionBoxRect.height > 5 && selectionBoxRect.width > 5 && e.button == 0){
          this.selectSectionsInSelectedArea(selectionBoxRect, e);
        }
        this.dragSelector = null;
    }
    
  }
  private handleUnlock(): void {
    const containers: Section[] = this.stateManager.getSelectedSections();    
    const state = this.stateManager.getState()
    let sectionsIds:string[] = [];
    containers.forEach(s => {
      s.isLocked = false;
      sectionsIds.push(s.id);

      // get drag control
      const dragDiv = DomHelper.getSectionDiv(s.id) as HTMLDivElement;
      if(dragDiv.classList.contains(CSS_CLASSES.LOCKED)){
        dragDiv.classList.remove(CSS_CLASSES.LOCKED)
      }
    });
    this.stateManager.setState({ sectionIds: sectionsIds, state: state, changeType: changeType.UPDATE_SECTION })
    LayoutManager.syncStateToDOM({state:state,changeType:changeType.UPDATE_ALL_Section})
  }

  private handleLock(): void {
    const containers: Section[] = this.stateManager.getSelectedSections();
    const state = this.stateManager.getState()
    let sectionsIds:string[] = [];
    containers.forEach(s => {
      s.isLocked = true;
      sectionsIds.push(s.id);
      
      // get drag control
      const dragDiv = DomHelper.getSectionDiv(s.id) as HTMLDivElement;
      if(!dragDiv.classList.contains(CSS_CLASSES.LOCKED)){
        dragDiv.classList.add(CSS_CLASSES.LOCKED)
      }
    });
    this.stateManager.setState({ sectionIds: sectionsIds, state: state, changeType: changeType.UPDATE_SECTION })
    LayoutManager.syncStateToDOM({state:state,changeType:changeType.UPDATE_ALL_Section})
  }
  private selectSectionsInSelectedArea(sa:DOMRect, ev:MouseEvent){
    this.domManager.clearSelections();
    const zoomValue = this.stateManager.getZoomValue();
    const state = this.stateManager.getState();
    const scale = (state.scale)?state.scale:[1,1]
    this.domManager.getAllElements().forEach(e => {
      if(e.offsetLeft > sa.left 
        && e.offsetLeft + e.offsetWidth * (zoomValue * scale[0]) < sa.left + sa.width 
        && e.offsetTop > sa.top
        && e.offsetTop + e.offsetHeight * (zoomValue * scale[1]) < sa.top + sa.height)
        {
          this.changeSelection(e, true, ev)
        }
        
    });
  }

  private setSectionsInState(
    sections: Section[],
    changeType: string,
    changedIds: string[],
  ) {
    let oldState = this.stateManager.getState()
    this.stateManager.setState({
      state: { ...oldState, sections: sections },
      changeType,
      sectionIds: changedIds,
    })
  }

  private fitToGrid(container: HTMLElement, changeSize:boolean=false): Section {
    const sectionObj = (container as any)['sectionObj']
    const state = this.stateManager.getState();
    const scale = (state.scale)?state.scale:[1,1]
    const cellWidth = (state.cellWidth / this.stateManager.getMultipler()) * scale[0] * this.stateManager.getZoomValue()
    const cellHeight = (state.cellHeight / this.stateManager.getMultipler()) * scale[1] * this.stateManager.getZoomValue()
    const newWidth = Math.round(container.offsetWidth / state.cellWidth) * state.cellWidth
    const newHeight =
      Math.round(container.offsetHeight / state.cellHeight) * state.cellHeight
    const newLeft = Math.round(container.offsetLeft / cellWidth) * cellWidth
    const newTop = Math.round(container.offsetTop / cellHeight) * cellHeight
    if(changeSize){
      container.style.width = `${newWidth}px`
      container.style.height = `${newHeight}px`
    }
    container.style.left = `${newLeft}px`
    container.style.top = `${newTop}px`
    if(changeSize){
      sectionObj.width = newWidth
      sectionObj.height = newHeight
    }
    
    sectionObj.colCount = Math.round(newWidth / state.cellWidth)
    sectionObj.rowCount = Math.round(newHeight / state.cellHeight)
    sectionObj.x = newLeft
    sectionObj.y = newTop
    sectionObj.row = Math.round(newTop / cellHeight)
    sectionObj.col = Math.round(newLeft / cellWidth)
    return sectionObj
  }

  private removeSectionFromState(sectionId: string) {
    let state = this.stateManager.getState()
    let sections = this.stateManager
      .getState()
      .sections.filter((x) => x.id != sectionId)
    this.stateManager.setState({
      sectionIds: [sectionId],
      state: { ...state, sections: sections },
      changeType: changeType.REMOVE_SECTION,
    })
  }
  private addOrUpdateSectionToState(
    sections: SectionBase[],
    changeType: string,
    idsChanged: string[],
  ): void {
    const layout = this.stateManager.getState()
    sections.forEach((section) => {
      let existingSection = layout.sections.find((el) => el.id === section.id)
      if (existingSection) {
        existingSection.row = section.row
        existingSection.rowCount = section.rowCount
        existingSection.colCount = section.colCount
        existingSection.col = section.col
        existingSection.height = section.height
        existingSection.width = section.width
        existingSection.selected = section.selected
        existingSection.propInfo = section.propInfo?.map((x: PropInfo) => x)
      } else {
        layout.sections.push(section.toJSON())
      }
    })
    this.stateManager.setState({
      sectionIds: idsChanged,
      state: { ...layout },
      changeType,
    })
  }

  private updateDragElementPosition(pageX: number, pageY: number): void {
    if (!this.dragElement) return

    const viewportContainer = DomHelper.getEl(
      `#${CSS_CLASSES.ID.VIEWPORT_CONTAINER}`,
    ) as HTMLElement

    const viewportRect = viewportContainer.getBoundingClientRect()

    // Calculate position relative to viewport
    let newLeft = pageX - viewportRect.left - this.offsetX
    let newTop = pageY - viewportRect.top - this.offsetY

    // Snap to grid
    const cellWidth = this.stateManager.getState().cellWidth
    const cellHeight = this.stateManager.getState().cellHeight
    newLeft = Math.round(newLeft / cellWidth) * cellWidth
    newTop = Math.round(newTop / cellHeight) * cellHeight

    this.dragElement.style.left = `${newLeft}px`
    this.dragElement.style.top = `${newTop}px`
  }

  private getValue(value: any) {
    if (!value) return value
    if (typeof value === 'string') {
      if (value.startsWith('rgb')) {
        return DomHelper.rgbToHex(value)
      }
    }
    if (!Number.isNaN(Number(value))) {
      return Number(value) + 'px'
    }
    return value
  }

  private initializeKeyboardShortcuts(): void {
    document.addEventListener('keydown', (event) => {
      if (!this.isFocusedInsideViewPort()) return
      let handledInternally = false
      if (event.key === 'Delete' || event.key === 'Backspace') {
        // Get all selected sections
        const selectedSectionIds = Array.from(
          document.querySelectorAll(SELECTORS.SELECTED_DRAG_ITEM),
        ).map((x) => x.id)
        handledInternally = true
        LayoutManager.removeSections(selectedSectionIds)
      }

      if (event.ctrlKey && event.key === 'z') {
        handledInternally = true
        this.stateManager.undo()
      }

      if (event.ctrlKey && event.key === 'y') {
        this.stateManager.redo()
      }

      if (event.ctrlKey && event.key === 'c') {
        handledInternally = true
        this.handleCopy()
      } else if (event.ctrlKey && event.key === 'v') {
        handledInternally = true
        this.handlePaste()
      }

      if (handledInternally) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
      }
    })
  }
}
